import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, BadgeCheck, Clock3, Loader2, Radio, Users, Award, HelpCircle, Sparkles, CalendarDays, Timer, Globe2, MonitorPlay, ListChecks, GraduationCap, Layers, Youtube } from "lucide-react";
import axios from "axios";
import EnrollModal from "@/components/EnrollModal";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BookingModal from "@/components/BookingModal";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const INCLUDES = {
  live: ["Live weekend sessions with the trainer", "1 year access to session recordings", "Hands-on S/4HANA system practice", "Assignments with personal feedback", "Weekly doubt clarification sessions", "Course completion certificate"],
  "self-paced": ["On-demand HD video lessons", "Full access for the listed duration", "Weekly doubt clarification sessions", "Configuration guides and PDFs", "Discussion forum access", "Certificate of completion"],
  certification: ["Structured week-by-week study plan", "Practice questions with explanations", "Mock exam simulations", "Doubt-clearing support", "Exam registration guidance", "Passing strategy session"],
};

const FAQS = {
  live: [
    { q: "What if I miss a live session?", a: "Every session is recorded and available in the LMS for 1 year, so you can catch up anytime." },
    { q: "Do I get system access?", a: "Yes — you get hands-on S/4HANA sandbox access for the duration of the program." },
    { q: "Is there post-course support?", a: "Weekly doubt sessions continue after the batch, plus a community forum for ongoing questions." },
  ],
  "self-paced": [
    { q: "How do doubt sessions work?", a: "Post questions on the discussion forum anytime, or join the weekly live doubt clarification call every Friday." },
    { q: "Can I extend my access?", a: "Yes — reach out before expiry and we'll extend at a discounted rate." },
    { q: "Do videos get updated?", a: "Yes — content is refreshed as SAP releases updates, at no extra cost during your access period." },
  ],
  certification: [
    { q: "Which exams does this cover?", a: "Current S/4HANA certification tracks — the study plan is tailored to your target exam code." },
    { q: "What is the pass rate?", a: "Alumni following the full plan pass at 98%+ on their first attempt." },
    { q: "Do you help with exam registration?", a: "Yes — we guide you through SAP Certification Hub registration and scheduling." },
  ],
};

const PREVIEW_VIDEOS = {
  "fico-live": "NqNqsXbWwtU",
  "ewm-live": "apKDLxg3VDQ",
  "cert-prep": "Bi_lT00-7Ok",
  "bp-free": "VmRSlVJq_D8",
  "fi-beginners": "hrAUuUB5Rv0",
  "co-videos": "Dqv07zLjD6A",
  "fi-co-combo": "qMd4c8bVrRI",
  "fscm-trm": "e9GR6pNbr_Y",
  "mm-videos": "R6JsxXIIv4Q",
  "sd-videos": "ffqBtIS60DY",
  "mm-sd-combo": "t085KbbZqqA",
  "rar-video": "lC7sH2ZyLsQ",
  "cfin-videos": "d-YlkMn-XCI",
  "fm-video": "HxWPuFQmWpM",
  "public-cloud-fin": "XdCuox2IIO8",
  "system-conversion": "N3EOFTQv9V4",
  "fiori": "9w-Ds5ccmnU",
  "abap": "b44VB1XaeNk",
  "fico-interview": "AnvV9TtkM2w",
  "e2e-impl": "DSNRXKY8unk",
  "pc-impl": "BFp8l3Nt27o",
  "unlimited": "gbLumJzx6DM",
};

const OfferBanner = ({ offer }) => (
  <div className="flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-5 mb-10" data-testid="course-offer-banner">
    <Sparkles size={16} className="mt-0.5 flex-shrink-0 text-amber-600" />
    <p className="text-sm text-amber-800 leading-relaxed"><span className="font-semibold">Limited-time offer — </span>{offer}</p>
  </div>
);

const SyllabusTab = ({ course, details }) => {
  const syllabus = details?.syllabus;
  const highlights = details?.highlights || INCLUDES[course.category] || INCLUDES["self-paced"];
  return (
    <div>
      {syllabus ? (
        <Accordion type="single" collapsible defaultValue="module-0" className="border-t border-slate-200" data-testid="course-syllabus-accordion">
          {syllabus.map((mod, i) => (
            <AccordionItem key={i} value={`module-${i}`} className="border-b border-slate-200" data-testid={`syllabus-module-${i}`}>
              <AccordionTrigger className="hover:no-underline py-5 group">
                <span className="flex items-center gap-5 text-left">
                  <span className="font-mono2 text-xl font-bold text-slate-300 group-hover:text-amber-600/60 transition-colors">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-sm sm:text-base font-semibold text-slate-900">{mod.module}</span>
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <ul className="pl-14 pr-2 pb-2 space-y-2.5">
                  {mod.topics.map((t) => (
                    <li key={t} className="flex items-start gap-2.5 text-sm text-slate-600 leading-relaxed">
                      <span className="mt-2 h-1 w-1 rounded-full bg-amber-500 flex-shrink-0" />
                      {t}
                    </li>
                  ))}
                </ul>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      ) : (
        <div className="border-t border-slate-200">
          {course.modules.map((m, i) => (
            <div key={m} className="flex items-center gap-5 py-5 border-b border-slate-200 group" data-testid={`course-module-${i}`}>
              <span className="font-mono2 text-xl font-bold text-slate-300 group-hover:text-amber-600/60 transition-colors">{String(i + 1).padStart(2, "0")}</span>
              <span className="text-sm sm:text-base text-slate-800">{m}</span>
            </div>
          ))}
        </div>
      )}

      <div className="mt-12">
        <h3 className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-6 flex items-center gap-2">
          <ListChecks size={13} /> Everything included
        </h3>
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4" data-testid="course-highlights">
          {highlights.map((inc) => (
            <div key={inc} className="flex items-start gap-3">
              <Award size={14} className="mt-1 flex-shrink-0 text-sky-600" />
              <span className="text-sm text-slate-600">{inc}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const FaqsTab = ({ faqs }) => (
  <div className="space-y-4" data-testid="course-faqs">
    {faqs.map((f) => (
      <div key={f.q} className="rounded-2xl border border-slate-200 bg-[#F8FAFC] p-5">
        <p className="font-semibold text-sm text-slate-900 mb-2 flex items-start gap-2">
          <HelpCircle size={14} className="mt-0.5 flex-shrink-0 text-amber-600" /> {f.q}
        </p>
        <p className="text-sm text-slate-600 leading-relaxed pl-6">{f.a}</p>
      </div>
    ))}
  </div>
);

const ScheduleTab = ({ course, details }) => {
  const schedule = details?.schedule;
  const who = details?.who;
  return (
    <div className="space-y-10">
      {schedule ? (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="course-schedule">
          {[
            { icon: CalendarDays, label: "Batch starts", value: schedule.start },
            { icon: Clock3, label: "Timings", value: schedule.time },
            { icon: CalendarDays, label: "Days", value: schedule.days },
            { icon: Timer, label: "Duration", value: schedule.duration },
            { icon: MonitorPlay, label: "Mode", value: schedule.mode },
            { icon: Globe2, label: "SAP version", value: details.version },
          ].filter((s) => s.value).map((s) => (
            <div key={s.label} className="rounded-2xl border border-slate-200 bg-[#F8FAFC] p-5">
              <p className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">
                <s.icon size={12} className="text-amber-600" /> {s.label}
              </p>
              <p className="text-sm font-semibold text-slate-900">{s.value}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="course-schedule">
          <div className="rounded-2xl border border-slate-200 bg-[#F8FAFC] p-5">
            <p className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">
              <Timer size={12} className="text-amber-600" /> Access & pace
            </p>
            <p className="text-sm font-semibold text-slate-900">{course.duration}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-[#F8FAFC] p-5">
            <p className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">
              <CalendarDays size={12} className="text-amber-600" /> Start
            </p>
            <p className="text-sm font-semibold text-slate-900">{course.batch}</p>
          </div>
          {details?.version && (
            <div className="rounded-2xl border border-slate-200 bg-[#F8FAFC] p-5">
              <p className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">
                <Globe2 size={12} className="text-amber-600" /> SAP version
              </p>
              <p className="text-sm font-semibold text-slate-900">{details.version}</p>
            </div>
          )}
        </div>
      )}

      {who && who.length > 0 && (
        <div data-testid="course-who">
          <h3 className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-6 flex items-center gap-2">
            <GraduationCap size={13} /> Who this course is for
          </h3>
          <div className="space-y-3">
            {who.map((w) => (
              <div key={w} className="flex items-start gap-3">
                <BadgeCheck size={14} className="mt-0.5 flex-shrink-0 text-amber-600" />
                <span className="text-sm text-slate-700 leading-relaxed">{w}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const CourseDetail = () => {
  const { id } = useParams();
  const [course, setCourse] = useState(null);
  const [related, setRelated] = useState([]);
  const [showEnroll, setShowEnroll] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setCourse(null);
    setNotFound(false);
    axios
      .get(`${API}/courses/${id}`)
      .then((r) => setCourse(r.data))
      .catch(() => setNotFound(true));
    axios
      .get(`${API}/courses`)
      .then((r) => setRelated(r.data.filter((c) => c.id !== id).slice(0, 3)))
      .catch(() => {});
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 px-6" data-testid="course-not-found">
        <p className="font-display text-2xl font-bold">Course not found</p>
        <Link to="/" className="bg-amber-400 text-slate-900 font-semibold text-sm px-8 py-3.5 rounded-full hover:bg-amber-500 transition-colors">
          Back to Catalog
        </Link>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="course-loading">
        <Loader2 size={32} className="text-amber-600 animate-spin" />
      </div>
    );
  }

  const details = course.details;
  const faqs = details?.faqs || FAQS[course.category] || FAQS["self-paced"];
  const previewId = PREVIEW_VIDEOS[course.id];

  return (
    <div className="relative min-h-screen" data-testid="course-detail-page">
      <div className="absolute inset-0 hero-grid-bg pointer-events-none -z-10" />
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-24">
        <Link to="/courses" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-amber-700 transition-colors mb-12" data-testid="course-back-link">
          <ArrowLeft size={15} /> Back to catalog
        </Link>

        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
              <p className="font-mono2 text-xs uppercase tracking-[0.25em] text-amber-600 mb-4 flex items-center gap-2">
                {course.category === "live" && <Radio size={12} />}
                {course.badge} — {course.level}
              </p>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight mb-5" data-testid="course-detail-title">
                {course.title}
              </h1>
              {details?.tagline && (
                <p className="text-base text-slate-600 leading-relaxed mb-6" data-testid="course-tagline">{details.tagline}</p>
              )}
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-10">
                <p className="flex items-center gap-2 text-sm text-slate-600">
                  <Clock3 size={14} className="text-slate-500" /> {course.duration}
                </p>
                <p className="flex items-center gap-2 text-sm text-slate-600">
                  <Users size={14} className="text-slate-500" /> {course.audience}
                </p>
                {details?.version && (
                  <p className="flex items-center gap-2 text-sm text-slate-600" data-testid="course-version">
                    <Layers size={14} className="text-slate-500" /> {details.version}
                  </p>
                )}
              </div>
            </motion.div>

            {details?.offer && <OfferBanner offer={details.offer} />}

            <motion.section initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, ease: EASE }} className="mb-14">
              <h2 className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-6">What you'll walk away with</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {(course.outcomes || []).map((o, i) => (
                  <div key={i} className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-[#F8FAFC] p-4">
                    <BadgeCheck size={15} className="mt-0.5 flex-shrink-0 text-amber-600" />
                    <span className="text-sm text-slate-700 leading-relaxed">{o}</span>
                  </div>
                ))}
              </div>
            </motion.section>

            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, ease: EASE }}>
              <Tabs defaultValue="syllabus" data-testid="course-tabs">
                <TabsList className="w-full justify-start rounded-full bg-slate-100 p-1.5 h-auto mb-8">
                  <TabsTrigger value="syllabus" className="flex-1 rounded-full text-xs sm:text-sm font-semibold px-4 py-2.5 data-[state=active]:bg-white data-[state=active]:text-amber-700 data-[state=active]:shadow-sm" data-testid="tab-syllabus">
                    Syllabus
                  </TabsTrigger>
                  <TabsTrigger value="faqs" className="flex-1 rounded-full text-xs sm:text-sm font-semibold px-4 py-2.5 data-[state=active]:bg-white data-[state=active]:text-amber-700 data-[state=active]:shadow-sm" data-testid="tab-faqs">
                    FAQs
                  </TabsTrigger>
                  <TabsTrigger value="schedule" className="flex-1 rounded-full text-xs sm:text-sm font-semibold px-4 py-2.5 data-[state=active]:bg-white data-[state=active]:text-amber-700 data-[state=active]:shadow-sm" data-testid="tab-schedule">
                    Schedule
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="syllabus">
                  <SyllabusTab course={course} details={details} />
                </TabsContent>
                <TabsContent value="faqs">
                  <FaqsTab faqs={faqs} />
                </TabsContent>
                <TabsContent value="schedule">
                  <ScheduleTab course={course} details={details} />
                </TabsContent>
              </Tabs>
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}
              className="lg:sticky lg:top-10 rounded-3xl border border-slate-200 bg-[#F8FAFC] p-8"
              data-testid="course-pricing-card"
            >
              {previewId && (
                <div className="mb-8" data-testid="course-preview-video">
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3 flex items-center gap-2">
                    <Youtube size={12} className="text-red-600" /> Free preview lesson
                  </p>
                  <div className="aspect-video rounded-2xl overflow-hidden border border-slate-200 bg-slate-100">
                    <iframe
                      src={`https://www.youtube-nocookie.com/embed/${previewId}`}
                      title={`${course.title} — free preview lesson`}
                      loading="lazy"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                    />
                  </div>
                </div>
              )}
              <div className="flex items-end gap-3 mb-2">
                {course.price === 0 ? (
                  <span className="font-mono2 text-4xl font-extrabold text-emerald-600">FREE</span>
                ) : (
                  <>
                    <span className="font-mono2 text-4xl font-extrabold text-slate-900">${course.price.toLocaleString()}</span>
                    {course.original_price && <span className="font-mono2 text-lg text-slate-500 line-through mb-1">${course.original_price.toLocaleString()}</span>}
                  </>
                )}
              </div>
              <p className="text-xs text-slate-500 mb-1">USD • one-time payment</p>
              <p className="font-mono2 text-[11px] text-amber-600 mb-8">{course.batch}</p>
              <button
                onClick={() => setShowEnroll(true)}
                className="w-full flex items-center justify-center gap-2 bg-amber-400 text-slate-900 font-semibold text-sm px-6 py-4 rounded-full hover:bg-amber-500 transition-colors mb-4"
                data-testid="course-detail-enroll-button"
              >
                {course.price > 0 ? "Enroll & Pay Securely" : "Enroll Free"} <ArrowUpRight size={15} />
              </button>
              <button
                onClick={() => window.dispatchEvent(new CustomEvent("open-booking"))}
                className="w-full border border-slate-200 text-slate-700 text-sm px-6 py-3.5 rounded-full hover:border-amber-400/50 hover:text-amber-700 transition-colors"
                data-testid="course-detail-book-call"
              >
                Questions? Book a free 15-min call
              </button>
              <div className="mt-8 pt-6 border-t border-slate-200 space-y-3">
                {course.features.map((f) => (
                  <div key={f} className="flex items-start gap-2.5 text-xs text-slate-600">
                    <BadgeCheck size={12} className="mt-0.5 flex-shrink-0 text-amber-600/70" />
                    {f}
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        {related.length > 0 && (
          <section className="mt-24">
            <h2 className="font-display text-2xl font-bold tracking-tight mb-8">More from the academy</h2>
            <div className="grid sm:grid-cols-3 gap-5">
              {related.map((r) => (
                <Link
                  key={r.id}
                  to={`/course/${r.id}`}
                  className="group rounded-3xl border border-slate-200 bg-[#F8FAFC] p-6 hover:-translate-y-1 hover:border-amber-400/40 transition-all duration-300"
                  data-testid={`related-course-${r.id}`}
                >
                  <p className="font-mono2 text-[9px] uppercase tracking-[0.18em] text-slate-500 mb-3">{r.badge}</p>
                  <h3 className="font-display text-base font-bold leading-snug mb-4 group-hover:text-amber-700 transition-colors">{r.title}</h3>
                  <span className="font-mono2 text-lg font-bold text-amber-600">{r.price === 0 ? "FREE" : `$${r.price.toLocaleString()}`}</span>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      {showEnroll && <EnrollModal course={course} onClose={() => setShowEnroll(false)} />}
      <Footer />
      <BookingModal />
    </div>
  );
};

export default CourseDetail;
